import * as React from "react";
import {useMemo, useState} from "react";
import {IEngagementTabResponse} from "../../../../api/AdminAnalyticsApi";
import {applySort, SortDir} from "../../../../utils/listUtil";
import {sortTh} from "../shared/SortableTh.component";
import {DOCUMENT_ENGAGEMENT_DESCRIPTION} from "../shared/analytics.constants";
import {numberWithCommas} from "../../../../utils/numberUtil";

interface IDocumentEngagementTableProps {
    documentOpens: IEngagementTabResponse["documentOpens"];
    totalWhitePaperOpens: number;
    totalMarketOutlookOpens: number;
    contentUniqueUsers: number;
}

export const DocumentEngagementTable: React.FC<IDocumentEngagementTableProps> = (props) => {
    const [docSort, setDocSort] = useState<{ col: string; dir: SortDir }>({col: "opens", dir: "desc"});
    const [showAllDocs, setShowAllDocs] = useState<boolean>(false);
    const sortedDocuments = useMemo(
        () => applySort(props.documentOpens, docSort.col, docSort.dir),
        [props.documentOpens, docSort]);

    return (
        <section className="admin-analytics__section" data-testid={"document-engagement"}>
            <div className="admin-analytics__section-header">
                <h2 className="admin-analytics__section-title">Content &amp; Insights Engagement</h2>
                <span className="admin-analytics__section-subtitle">
                    White Papers and Market Outlooks &mdash; which documents are being read?
                </span>
            </div>
            <p className="admin-analytics__section-desc">{DOCUMENT_ENGAGEMENT_DESCRIPTION}</p>
            <div className="admin-analytics__summary-grid">
                <div className="admin-analytics__summary-card admin-analytics__summary-card--total">
                    <div className="admin-analytics__summary-label">Total Opens </div>
                    <div className="admin-analytics__summary-value">
                        {numberWithCommas(props.totalWhitePaperOpens + props.totalMarketOutlookOpens)}
                    </div>
                    <div className="admin-analytics__summary-note"> total document views</div>
                </div>
                <div className="admin-analytics__summary-card admin-analytics__summary-card--internal">
                    <div className="admin-analytics__summary-label">White Papers </div>
                    <div className="admin-analytics__summary-value">
                        {numberWithCommas(props.totalWhitePaperOpens)}
                    </div>
                    <div className="admin-analytics__summary-note"> white paper opens</div>
                </div>
                <div className="admin-analytics__summary-card admin-analytics__summary-card--external">
                    <div className="admin-analytics__summary-label">Market Outlooks </div>
                    <div className="admin-analytics__summary-value">
                        {numberWithCommas(props.totalMarketOutlookOpens)}
                    </div>
                    <div className="admin-analytics__summary-note"> market outlook opens</div>
                </div>
            </div>
            <div className="admin-analytics__summary-grid" style={{marginTop: 12}}>
                <div className="admin-analytics__summary-card admin-analytics__summary-card--total">
                    <div className="admin-analytics__summary-label">Unique Readers </div>
                    <div className="admin-analytics__summary-value">
                        {numberWithCommas(props.contentUniqueUsers)}
                    </div>
                    <div className="admin-analytics__summary-note"> distinct users who opened content
                    </div>
                </div>
                <div className="admin-analytics__summary-card admin-analytics__summary-card--internal">
                    <div className="admin-analytics__summary-label">Documents Accessed </div>
                    <div className="admin-analytics__summary-value">{props.documentOpens.length}</div>
                    <div className="admin-analytics__summary-note"> unique documents opened</div>
                </div>
            </div>
            <table className="admin-analytics__table">
                <thead>
                <tr>
                    {sortTh("documentName", "Document", docSort, setDocSort, undefined, "asc")}
                    {sortTh("documentType", "Type", docSort, setDocSort, undefined, "asc")}
                    {sortTh("opens", "Opens - Total", docSort, setDocSort, "admin-analytics__cell--right")}
                    {sortTh("internalOpens", "Opens - Internal", docSort, setDocSort,
                        "admin-analytics__cell--right")}
                    {sortTh("externalOpens", "Opens - External", docSort, setDocSort,
                        "admin-analytics__cell--right")}
                    {sortTh("uniqueInternalUsers", "Unique Readers - Internal", docSort, setDocSort,
                        "admin-analytics__cell--right")}
                    {sortTh("uniqueExternalUsers", "Unique Readers - External", docSort, setDocSort,
                        "admin-analytics__cell--right")}
                </tr>
                </thead>
                <tbody>
                {(showAllDocs ? sortedDocuments : sortedDocuments.slice(0, 10)).map((row) => (
                    <tr key={row.documentPath}>
                        <td>{row.documentName}</td>
                        <td>
                            <span
                                className={`admin-analytics__badge admin-analytics__badge--${
                                    row.documentType === "White Paper" ? "wp" : "mo"}`}
                            >{row.documentType}</span>
                        </td>
                        <td className="admin-analytics__cell--right">{numberWithCommas(row.opens)}</td>
                        <td className="admin-analytics__cell--right">{numberWithCommas(row.internalOpens)}</td>
                        <td className="admin-analytics__cell--right">{numberWithCommas(row.externalOpens)}</td>
                        <td className="admin-analytics__cell--right">
                            {numberWithCommas(row.uniqueInternalUsers)}
                        </td>
                        <td className="admin-analytics__cell--right">
                            {numberWithCommas(row.uniqueExternalUsers)}
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
            {props.documentOpens.length > 10 && (
                <button className="admin-analytics__show-more-btn"
                        onClick={() => setShowAllDocs(!showAllDocs)}>
                    {showAllDocs ? "Show Top 10" : `Show All ${props.documentOpens.length} Documents`}
                </button>
            )}
        </section>
    );
};
